import React from 'react';
import PropTypes from 'prop-types';
import Tippy from '@tippyjs/react';
import { Announcement01, Building07, LayoutGrid01, MarkerPin02, Passport, UserSquare } from '@opendate-app/setlist';
import { Calendar as CalendarIcon } from '@untitled-ui/icons-react';
import { offsetDateForBrowser } from '../../../../shared/timeZoneLogic';

const moment = require('moment');

const formatDate = (time, tz) => moment(offsetDateForBrowser(new Date(time), tz)).format('MM/DD/YY');
const formatDateTime = (time, tz) => offsetDateForBrowser(new Date(time), tz).toLocaleString('en-US', {
  weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric',
});

const ageLabel = (age) => (age === 'Under 18 must be accompanied by parent or guardian' ? 'Under 18 w/ Guardian' : age);

const SimpleHeaderPills = ({ confirm, offer }) => {
  const pills = [];

  // A draft offer not yet linked to an event has no `confirm`; fall back to the
  // offer's own venue / location / age so the header still carries context.
  if (!confirm) {
    if (offer?.venue?.name) {
      pills.push(
        <Tippy key="venue" content={offer.venue.name}>
          <span className="badge badge-confirm-metadata"><Building07 width={16} height={16} className="badge-confirm-metadata-icon" aria-hidden="true" />{offer.venue.name}</span>
        </Tippy>,
      );
    }
    if (offer?.venue?.city_and_state) {
      pills.push(
        <Tippy key="location" content="Location">
          <span className="badge badge-confirm-metadata"><MarkerPin02 width={16} height={16} className="badge-confirm-metadata-icon" aria-hidden="true" />{offer.venue.city_and_state}</span>
        </Tippy>,
      );
    }
    if (offer?.age_restriction) {
      pills.push(
        <Tippy key="age" content="Age Restriction">
          <span className="badge badge-confirm-metadata"><Passport width={16} height={16} className="badge-confirm-metadata-icon" aria-hidden="true" />{ageLabel(offer.age_restriction)}</span>
        </Tippy>,
      );
    }
    if (pills.length === 0) return null;
    return <div className="simple-header-pills" data-testid="simple-header-pills">{pills}</div>;
  }

  const tz = confirm.venue_ownership?.tzinfo_identifier;
  const holds = (confirm.hold_positions || []).slice().sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
  const eventTypeTags = (confirm.canonical_tags || []).filter(
    (t) => t.category === 'event_type' || t.category_key === 'event_type',
  );

  eventTypeTags.forEach((tag, i) => {
    pills.push(
      <span key={`type-${tag.id || i}`} className="simple-header-pill simple-header-pill--type">
        {tag.name}
      </span>,
    );
  });

  if (confirm.type === 'Confirm' && confirm.start_time) {
    pills.push(
      <Tippy key="date" content="Date and Time">
        <span className="badge badge-confirm-metadata">
          <CalendarIcon width={16} height={16} className="badge-confirm-metadata-icon" aria-hidden="true" />
          {confirm.multi_day ? (confirm.human_date_and_time_with_day_of_week || formatDateTime(confirm.start_time, tz)) : formatDateTime(confirm.start_time, tz)}
        </span>
      </Tippy>,
    );
  } else if (confirm.type === 'Hold' && holds.length > 0) {
    pills.push(
      <Tippy key="holds" content={holds.map((hp, i) => <div key={i}>{`H${hp.position}`} - {formatDate(hp.start_time, tz)}</div>)}>
        <span className="badge badge-confirm-metadata">
          <CalendarIcon width={16} height={16} className="badge-confirm-metadata-icon" aria-hidden="true" />
          {holds.length === 1 ? (
            <span><strong className="mr-1">1 Hold</strong>{formatDate(holds[0].start_time, tz)}</span>
          ) : (
            <span><strong className="mr-1">{holds.length} Holds</strong>{formatDate(holds[0].start_time, tz)} - {formatDate(holds[holds.length - 1].start_time, tz)}</span>
          )}
        </span>
      </Tippy>,
    );
  }

  if (confirm.venue && confirm.venue_ownership_rooms_count === 1) {
    pills.push(
      <Tippy key="venue" content={confirm.venue.name}>
        <span className="badge badge-confirm-metadata"><Building07 width={16} height={16} className="badge-confirm-metadata-icon" aria-hidden="true" />{confirm.venue.name}{confirm.rooms?.[0] ? ` - ${confirm.rooms[0].name}` : ''}</span>
      </Tippy>,
    );
  } else if (confirm.venue && confirm.rooms) {
    (confirm.rooms || []).slice().sort((a, b) => a.name.localeCompare(b.name)).forEach((room, i) => {
      pills.push(
        <Tippy key={`room-${i}`} content={confirm.rooms.length > 1 ? `Room ${i + 1}` : 'Room'}>
          <span className="badge badge-confirm-metadata"><Building07 width={16} height={16} className="badge-confirm-metadata-icon" aria-hidden="true" />{confirm.venue.name} - {room.name}</span>
        </Tippy>,
      );
    });
  }

  if (offer?.age_restriction) {
    pills.push(
      <Tippy key="age" content="Age Restriction">
        <span className="badge badge-confirm-metadata">
          <Passport width={16} height={16} className="badge-confirm-metadata-icon" aria-hidden="true" />
          {ageLabel(offer.age_restriction)}
        </span>
      </Tippy>,
    );
  }

  if (confirm.venue?.city_and_state) {
    pills.push(
      <Tippy key="location" content="Location">
        <span className="badge badge-confirm-metadata"><MarkerPin02 width={16} height={16} className="badge-confirm-metadata-icon" aria-hidden="true" />{confirm.venue.city_and_state}</span>
      </Tippy>,
    );
  }

  if (confirm.buyer?.name) {
    pills.push(
      <Tippy key="buyer" content="Buyer">
        <span className="badge badge-confirm-metadata"><UserSquare width={16} height={16} className="badge-confirm-metadata-icon" aria-hidden="true" />{confirm.buyer.name}</span>
      </Tippy>,
    );
  }

  if (confirm.promoter?.name) {
    pills.push(
      <Tippy key="promoter" content="Promoter">
        <span className="badge badge-confirm-metadata"><Announcement01 width={16} height={16} className="badge-confirm-metadata-icon" aria-hidden="true" />{confirm.promoter.name}</span>
      </Tippy>,
    );
  }

  if (confirm.event_template?.name) {
    pills.push(
      <Tippy key="template" content="Event Template">
        <span className="badge badge-confirm-metadata"><LayoutGrid01 width={16} height={16} className="badge-confirm-metadata-icon" aria-hidden="true" />{confirm.event_template.name}</span>
      </Tippy>,
    );
  }

  if (pills.length === 0) return null;

  return (
    <div className="simple-header-pills" data-testid="simple-header-pills">
      {pills}
    </div>
  );
};

SimpleHeaderPills.propTypes = {
  confirm: PropTypes.object,
  offer: PropTypes.object,
};

export default SimpleHeaderPills;
